import { createFileRoute, Link } from "@tanstack/react-router";
import scalpelAsset from "@/assets/scalpel-news.webp.asset.json";
import scalpelAsset256 from "@/assets/scalpel-news-256.webp.asset.json";

const scalpelImg = scalpelAsset.url;
const scalpelImg256 = scalpelAsset256.url;
import { PageSection } from "@/components/site/PageSection";
import { EsPageShell } from "@/components/site/EsPageShell";
import { esHead, SITE } from "@/lib/es-pages";
import { listEsPosts } from "@/lib/posts-es";

const BLOG_PATH = "/es/el-doctor-analiza-las-noticias";
const BLOG_URL = `${SITE}${BLOG_PATH}`;

export const Route = createFileRoute("/es/el-doctor-analiza-las-noticias")({
  head: () => {
    const base = esHead("blog");
    const first = listEsPosts()[0];
    const firstThumb = first?.featuredImageThumb ?? first?.featuredImage;
    return {
      meta: base.meta,
      links: [
        ...base.links,
        ...(firstThumb
          ? [
              {
                rel: "preload",
                as: "image",
                href: firstThumb,
                fetchPriority: "high",
              } as const,
            ]
          : []),
      ],
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Blog",
                "@id": `${BLOG_URL}#blog`,
                name: "El Dr. Fitzer Analiza las Noticias",
                url: BLOG_URL,
                description:
                  "El Dr. Matthew Fitzer analiza las últimas noticias sobre cirugía bariátrica, pérdida de peso y nutrición.",
                inLanguage: "es-US",
                translationOfWork: {
                  "@id": `${SITE}/dr-fitzer-dissects-the-news#blog`,
                },
                isPartOf: { "@id": `${SITE}/#website` },
                publisher: { "@id": `${SITE}/#organization` },
              }),
            },
          ]
        : [],
    };
  },
  component: EsNewsIndex,
});

function formatDateEs(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("es-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function EsNewsIndex() {
  const posts = listEsPosts();

  return (
    <EsPageShell
      page="blog"
      title="El Dr. Fitzer Analiza las Noticias"
      intro="El Dr. Fitzer escribe sobre la literatura profesional y las noticias de salud relacionadas con la cirugía bariátrica, la pérdida de peso y la nutrición, analizando en qué puede confiar y qué debe poner en duda."
      banner={
        <img
          src={scalpelImg256}
          srcSet={`${scalpelImg256} 256w, ${scalpelImg} 512w`}
          sizes="(min-width: 768px) 192px, (min-width: 640px) 160px, 128px"
          alt="Mano enguantada sosteniendo un bisturí"
          width={512}
          height={415}
          className="mx-auto mt-4 h-auto w-32 sm:w-40"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      }
    >
      <PageSection className="py-4" maxWidth="4xl">
        <ol className="space-y-10">
          {posts.map((post, i) => {
            const thumb = post.featuredImageThumb ?? post.featuredImage;
            return (
              <li key={post.slug}>
                <article>
                  {thumb && (
                    <Link
                      to="/es/el-doctor-analiza-las-noticias/$slug"
                      params={{ slug: post.slug }}
                      aria-hidden="true"
                      tabIndex={-1}
                    >
                      <img
                        src={thumb}
                        srcSet={post.featuredImageThumbSrcSet}
                        sizes="192px"
                        alt=""
                        width={448}
                        height={252}
                        className="mb-3 w-48 h-auto rounded-md"
                        loading={i === 0 ? "eager" : "lazy"}
                        fetchPriority={i === 0 ? "high" : "auto"}
                        decoding="async"
                      />
                    </Link>
                  )}
                  <h2 className="font-serif text-2xl text-brand-mid">
                    <Link
                      to="/es/el-doctor-analiza-las-noticias/$slug"
                      params={{ slug: post.slug }}
                      className="hover:underline"
                    >
                      {post.title}
                    </Link>
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    <time dateTime={post.date}>{formatDateEs(post.date)}</time>
                  </p>
                  {post.excerpt && (
                    <p className="mt-2 text-foreground/80">{post.excerpt}</p>
                  )}
                  <Link
                    to="/es/el-doctor-analiza-las-noticias/$slug"
                    params={{ slug: post.slug }}
                    className="mt-2 inline-block font-medium text-brand-mid hover:underline"
                  >
                    Leer más →
                  </Link>
                </article>
              </li>
            );
          })}
        </ol>
      </PageSection>
    </EsPageShell>
  );
}
